import BreakpointsService from "@omni/services/breakpoint/breakpoint.service";
import { BreakpointMinSizes } from "@omni/types/enums/breakPoint.enum";
import { isDefined } from "@omni/utils/helper.utils";
import { isEmpty } from "lodash-es";
import React, {
    Context,
    PropsWithChildren,
    ReactNode,
    use,
    useEffect,
    useState
} from "react";

export type BreakpointMatches = {
    [key in BreakpointMinSizes]?: boolean;
};

type mediaQueryList = {
    [key in BreakpointMinSizes]?: any;
};

export type BreakpointContextValues = {
    breakpoints: BreakpointMatches;
};

const defaultContext: BreakpointContextValues = {
    breakpoints: {}
};

const BreakpointContext: Context<BreakpointContextValues> =
    React.createContext<BreakpointContextValues>(defaultContext);

type Props = {
    children?: ReactNode;
};

const BreakpointProvider = ({ children }: Props) => {
    const breakpointsObject = BreakpointsService.getBreakpointsObject()?.min;
    const [queryMatch, setQueryMatch] = useState<BreakpointMatches>({});
    const [isAttached, setIsAttached] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        const mediaQueryLists: mediaQueryList = {};
        const keys: BreakpointMinSizes[] = Object.keys(
            breakpointsObject
        ) as BreakpointMinSizes[];

        const handleQueryListener = () => {
            const updatedMatches: BreakpointMatches = keys.reduce(
                (acc: BreakpointMatches, media) => {
                    acc[media as BreakpointMinSizes] =
                        !!mediaQueryLists[media]?.matches;
                    return acc;
                },
                {}
            );

            setQueryMatch(updatedMatches);
        };

        if (window?.matchMedia && !isAttached) {
            const matches: mediaQueryList = {};
            keys.forEach((media) => {
                mediaQueryLists[media] = window.matchMedia(
                    breakpointsObject[media]
                );
                matches[media] = mediaQueryLists[media].matches;
            });
            setQueryMatch(matches);
            setIsAttached(true);
            keys.forEach((media) => {
                mediaQueryLists[media].addEventListener(
                    "change",
                    handleQueryListener
                );
            });
        }

        return () => {
            if (isAttached) {
                keys.forEach((media) => {
                    mediaQueryLists[media].removeEventListener(
                        "change",
                        handleQueryListener
                    );
                });
            }
        };
    }, []);

    return (
        <BreakpointContext value={{ breakpoints: queryMatch }}>
            {children}
        </BreakpointContext>
    );
};

/**
 * HOC that injects breakpoint context onto wrapped component.
 *
 * It provides the `BreakpointContext` and a safety boundary that waits
 * for the breakpoint context to initialize. It ensures that hooks like
 * `useBreakpoint` and `useIsBreakpoint` can be used inside the wrapped
 * component without returning `undefined` during the initial render.
 *
 * If the breakpoint context has not yet been initialized and a
 * `FallbackComponent` is provided, that fallback will be rendered instead
 * of the wrapped component. You can use this to render a skeleton on the
 * server.
 *
 * @template T - Props of the wrapped component.
 *
 * @param {React.ComponentType<T>} WrappedComponent
 *   The component that will receive breakpoint context.
 *
 * @param {React.FC} [FallbackComponent]
 *   Optional component to render while breakpoints are still uninitialized.
 *   Useful for preventing undefined values from `useBreakpoint`-based hooks.
 *
 * @returns {React.FC<T>}
 *   A new component that wraps `WrappedComponent` with breakpoint context.
 */
const withBreakpoints = <T extends {}>(
    WrappedComponent: React.ComponentType<T>,
    FallbackComponent?: React.FC
) => {
    const BreakpointsInitialzedBoundary = ({
        children
    }: PropsWithChildren<{}>) => {
        const { breakpoints } = useBreakpoint();

        if (isEmpty(breakpoints) && isDefined(FallbackComponent)) {
            return <FallbackComponent />;
        }

        return children;
    };

    const ComponentWithBreakpoints = (props: T) => {
        return (
            <BreakpointProvider>
                <BreakpointsInitialzedBoundary>
                    <WrappedComponent {...props} />
                </BreakpointsInitialzedBoundary>
            </BreakpointProvider>
        );
    };

    return ComponentWithBreakpoints;
};

/**
 * Hook that returns the current breakpoint context values.
 *
 * Must be used within a `BreakpointProvider`. If called outside of the provider,
 * this hook throws an error.
 *
 * @returns {BreakpointContextValues}
 *   The current breakpoint context, including the resolved breakpoint booleans.
 *
 * @throws {Error}
 *   Throws if the hook is used outside of `BreakpointProvider`.
 */
const useBreakpoint = (): BreakpointContextValues => {
    const context = use<BreakpointContextValues>(BreakpointContext);
    if (context === undefined) {
        throw new Error(
            "useBreakpointContext can only be used inside BreakpointProvider"
        );
    }
    return context;
};

/**
 * Hook that returns whether a specific breakpoint is currently active,
 * along with whether the breakpoints are initialzed.
 *
 * Must be used within a `BreakpointProvider`. If called outside of the provider,
 * this hook throws an error. On the initial render, the hook
 * returns `{ matches: false, ready: false }`.
 *
 * @param {keyof typeof BreakpointMinSizes} breakpoint
 *   The breakpoint key to check (e.g. `"sm"`, `"md"`, `"lg"`).
 *
 * @returns {{ matches: boolean, ready: boolean }}
 *   - `matches`: `true` if the breakpoint is currently active, otherwise `false`.
 *   - `ready`: `true` once the breakpoint state has initialized,
 *               `false` on the initial render.
 */
const useIsBreakpoint = (breakpoint: keyof typeof BreakpointMinSizes) => {
    const { breakpoints } = useBreakpoint();

    return {
        matches: !!breakpoints[breakpoint],
        ready: isDefined(breakpoints[breakpoint])
    };
};

export {
    BreakpointContext,
    BreakpointProvider,
    useBreakpoint,
    useIsBreakpoint,
    withBreakpoints
};
